import {useTranslation} from '@aboutyou/i18n';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {
  useBasketRoute,
  useSearchRoute,
  useWishlistRoute,
} from '@aboutyou/router';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {MOBILE_HEADER_HEIGHT} from '../../../constants/heights';
import {Z_INDEXES} from '../../../constants/layers';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {DISCOUNT_TOAST_PORTAL_ID} from '../../molecules/PromotionBanner/DiscountToast';
import {HeaderButton} from './HeaderButton';
import {HeaderLink} from './HeaderLink';
import {HeaderIconName} from './assets';

export interface HeaderProps {
  onMenuClick(): void;
  wishlistCount: number;
  wishlistWithBadge?: {
    color: string;
  };
  basketWithBadge?: {
    color: string;
  };
  basketCount: number;
  className?: string;
  isCheckout: boolean;
  logoComponent: React.ReactNode;
  shopHintComponents?: React.ReactNode;
  trackIconClick?: (label: string) => void;
  NotificationIconComponent?: React.ReactNode;
}

export const Header = ({
  onMenuClick,
  wishlistCount,
  wishlistWithBadge,
  basketWithBadge,
  basketCount,
  className,
  isCheckout,
  logoComponent,
  trackIconClick,
  shopHintComponents,
  NotificationIconComponent,
}: HeaderProps) => {
  const {t} = useTranslation();
  const basketRoute = useBasketRoute();
  const wishlistRoute = useWishlistRoute();
  const searchRoute = useSearchRoute();
  const testId =
    wishlistCount > 0 ? 'HeaderWishlist_filled' : 'HeaderWishlist_empty';
  const isHydrationDone = useHydrationDone();

  return (
    <HeaderContainer id="header" className={className}>
      <BurgerMenu
        data-testid="HeaderMenu"
        aria-label={t('Open the page menu')}
        asset={HeaderIconName.Menu}
        onClick={onMenuClick}
        isCheckout={isCheckout}
        iconSize="SS"
      />
      {logoComponent}
      <HeaderButton
        data-testid="HeaderSearch"
        asset={GenericIconName.NavHeaderSearch}
        to={searchRoute}
        isCheckout={isCheckout}
        iconSize="S"
        aria-label={t('Go to the search page')}
      />
      {!isCheckout && NotificationIconComponent}
      <HeaderButton
        data-testid={testId}
        count={wishlistCount}
        countLabel={
          wishlistCount === 1
            ? t('1 item')
            : t('{count} items', {count: wishlistCount})
        }
        asset={GenericIconName.NavHeaderWishlist}
        to={wishlistRoute}
        isCheckout={isCheckout}
        trackClick={() => trackIconClick?.('wishlist_icon')}
        iconSize="S"
        badgeOffset={{top: 2, right: 0}}
        showBadge={Boolean(wishlistWithBadge)}
        badgeColor={wishlistWithBadge?.color}
        aria-label={t('Go to the wishlist page')}
      />
      <HeaderButton
        data-testid="HeaderBasket"
        count={basketCount}
        countLabel={
          basketCount === 1
            ? t('1 item')
            : t('{count} items', {count: basketCount})
        }
        asset={GenericIconName.NavHeaderBasket}
        to={basketRoute}
        isCheckout={isCheckout}
        trackClick={() => trackIconClick?.('basket_icon')}
        iconSize="S"
        badgeOffset={{top: 2, right: 2}}
        showBadge={Boolean(basketWithBadge)}
        badgeColor={basketWithBadge?.color}
        aria-label={t('Go to the basket page')}
        countOffset={22}
      />
      {isCheckout && isHydrationDone && (
        <HeaderLink to="/h" target="_blank">
          {t('Help')}
        </HeaderLink>
      )}
      <ShopHintsContainer>
        {shopHintComponents}
        <div id={DISCOUNT_TOAST_PORTAL_ID} />
      </ShopHintsContainer>
    </HeaderContainer>
  );
};

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  z-index: ${Z_INDEXES.MAIN_HEADER};
  width: 100%;
  box-sizing: border-box;
  display: flex;
  height: ${MOBILE_HEADER_HEIGHT}px;
  align-items: center;
  padding: 8px 5px 7px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.04), 0 0 2px 0 rgba(0, 0, 0, 0.2);
  background-color: ${COLORS.White};
`;

const ShopHintsContainer = styled.div`
  position: absolute;
  top: 100%;
  margin: 0 10px;
  width: calc(100vw - 30px);
  margin-top: 16px;
`;

const BurgerMenu = styled(HeaderButton)`
  padding-inline: 2px;
`;
